<template>
  <small-dialog
    ref="dialog"
    :value="value"
    data-testid="login-dialog"
    content-class="non-selectable"
    content-style="width: 350px; max-width: 100%"
    v-bind="$attrs"
    persistent
  >
    <template v-slot:header>
      <q-tabs
        v-if="!user && customerID"
        v-model="tab"
        active-color="primary"
        indicator-color="primary"
        align="justify"
        filled
      >
        <q-tab name="login" :label="$t('Log In')" />
        <q-tab name="register" :label="$t('Register')" />
      </q-tabs>

      <dialog-header
        v-else
        icon="user"
        :title="title || $t('Log In')"
        :color="color || 'primary'"
        no-close-btn
      />
    </template>

    <q-list class="overflow-hidden">
      <q-item v-if="showRegisterHint">
        <q-item-section>
          <q-item-label>
            {{ $t("hint.registerCustomerAccount") }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <q-form ref="form" greedy>
        <q-input
          v-model="email"
          :label="$t('Email Address')"
          @keydown.enter.prevent="submit"
          autocomplete="email"
          :color="color || 'primary'"
          :rules="[(a) => a.trim().length > 0]"
          hide-bottom-space
          item-aligned
        >
          <template v-slot:prepend>
            <q-icon name="email" />
          </template>
        </q-input>
        <q-input
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          :label="$t('Password')"
          @keydown.enter.prevent="submit"
          :autocomplete="(tab === 'login' ? 'current' : 'new') + '-password'"
          :color="color || 'primary'"
          :rules="[(a) => a.length > 0]"
          hide-bottom-space
          item-aligned
        >
          <template v-slot:prepend>
            <q-icon name="lock" />
          </template>
          <template v-slot:append>
            <q-icon
              :name="showPassword ? 'visible' : 'visible_off'"
              @click="showPassword = !showPassword"
              class="q-field__focusable-action"
            />
          </template>
        </q-input>
      </q-form>
    </q-list>

    <template v-slot:footer>
      <q-card-actions>
        <q-btn
          v-if="tab === 'login'"
          @click="resetPassword"
          :label="$t('Reset Password')"
          :disable="!email.trim().length"
          :color="color || 'primary'"
          flat
        />
        <div class="col-grow" />
        <q-btn
          v-if="user"
          @click="logOut"
          :label="$t('Log Out')"
          :color="color || 'primary'"
          flat
        />
        <q-btn
          @click="submit"
          :label="$t(tab === 'login' ? 'Log In' : 'Register')"
          :color="color || 'primary'"
          :loading="loading"
          :flat="!isValid"
        />
      </q-card-actions>
    </template>
  </small-dialog>
</template>

<script>
export default {
  name: "LogIn",
  props: ["value", "title", "color"],
  data() {
    return {
      tab: "login",
      email: "",
      password: "",
      loading: false,
      showPassword: false,
    };
  },
  created() {
    this.reset();
  },
  computed: {
    user() {
      return this.$store.state.main.user;
    },
    customerID() {
      return this.$route.name?.startsWith("customer")
        ? this.$route.params.customerID
        : null;
    },
    // Set by customer links (e.g. a quote emailed to a contact without a
    // registered account) to invite the recipient to set up their account. The
    // value is their email address when the sender could determine it
    // unambiguously, otherwise a flag with no address to prefill.
    registerParam() {
      if (this.user || !this.customerID) {
        return null;
      }
      const value = this.$route.query?.register;
      return typeof value === "string" && value.trim().length
        ? value.trim()
        : null;
    },
    registerEmail() {
      return this.registerParam && this.registerParam.includes("@")
        ? this.registerParam
        : null;
    },
    showRegisterHint() {
      return Boolean(this.registerParam) && this.tab === "register";
    },
    isValid() {
      return Boolean(this.email.trim().length && this.password.length);
    },
  },
  methods: {
    reset() {
      this.email = this.registerEmail || "";
      this.password = "";
      this.tab = this.registerParam ? "register" : "login";
    },
    logOut() {
      return this.$store.dispatch("LOG_OUT");
    },
    logIn() {
      return this.$store.dispatch("LOG_IN", {
        email: this.email.trim(),
        password: this.password,
      });
    },
    async submit() {
      if (!this.isValid) {
        this.$refs.form.validate();
        return;
      }

      this.loading = true;
      try {
        if (this.tab === "login") {
          await this.logIn();
        } else {
          await this.$store.dispatch("REGISTER_CUSTOMER", {
            uid: this.customerID,
            email: this.email.trim(),
            password: this.password,
          });
          await this.logIn();
          this.$store.dispatch(
            "NOTIFY_SUCCESS",
            this.$t("success.customerAccountCreated")
          );
          this.$dialogs.push({ name: "account" });
        }
      } catch (error) {
        this.$store.dispatch("NOTIFY_ERROR", error);
      } finally {
        this.loading = false;
      }
    },
    resetPassword() {
      this.$store.dispatch("PROMPT", {
        title: this.$t("Confirm"),
        message: this.$t("confirm.reset-password", {
          email: this.email.trim(),
        }),
        ok: this.$t("OK"),
        cancel: this.$t("Cancel"),
        success: () => {
          this.loading = true;
          this.$store
            .dispatch("RESET_PASSWORD", this.email.trim())
            .then(() => {
              this.loading = false;
              this.$store.dispatch("NOTIFY_SUCCESS", "resetPasswordSent");
            })
            .catch((error) => {
              this.loading = false;
              this.$store.dispatch("NOTIFY_ERROR", error);
            });
        },
      });
    },
  },
  watch: {
    value(isVisible) {
      if (isVisible) {
        this.reset();
      }
    },
    registerParam() {
      this.reset();
    },
  },
  mounted() {
    this.$q.loading.hide();
  },
};
</script>
